import React from 'react';
import { Link } from 'react-router-dom';

const ChessMatePrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            ChessMate by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: 17 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Wolf Technologies Ltd ("we", "us", "our") operates ChessMate, an online chess platform
              available as a mobile application and web service. This Privacy Policy explains how we
              collect, use, store, and protect your personal data when you use ChessMate.
            </p>
            <p>
              By creating an account or using ChessMate, you agree to the collection and use of
              information in accordance with this policy. If you do not agree, please do not use the app.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>We collect the following data when you use ChessMate:</p>
            <ul>
              <li><strong>Email address</strong> — used for account creation, authentication, and communication.</li>
              <li><strong>Display name</strong> — shown on your player profile and in-game.</li>
              <li><strong>Game history</strong> — record of games played, moves made, and results.</li>
              <li><strong>Puzzle progress</strong> — tracking which puzzles you have attempted and solved.</li>
              <li><strong>Subscription status</strong> — managing your active subscription tier (Gold, Platinum, or Diamond).</li>
              <li><strong>Chess ratings</strong> — Elo ratings across different time controls and game modes.</li>
              <li><strong>Device information</strong> — basic device type and OS version for app compatibility.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>ChessMate does <strong>not</strong> collect, access, or store:</p>
            <ul>
              <li><strong>Location data</strong> or GPS coordinates</li>
              <li><strong>Contacts</strong> or address book information</li>
              <li><strong>Photos, camera, or media</strong> library content</li>
              <li><strong>Biometric data</strong> (fingerprints, face scans, etc.)</li>
              <li><strong>Health or fitness data</strong></li>
              <li><strong>Financial information</strong> (payment details are handled securely by third-party processors)</li>
              <li><strong>Browsing history</strong> outside of ChessMate</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>We use the data we collect for the following purposes:</p>
            <ul>
              <li><strong>Provide chess gameplay</strong> — facilitating live multiplayer games, AI opponent matches, and game variants.</li>
              <li><strong>Matchmaking</strong> — pairing you with opponents of a similar skill level using your Elo rating.</li>
              <li><strong>Track your progress</strong> — recording game history, puzzle completion, lesson progress, and rating changes over time.</li>
              <li><strong>Manage subscriptions</strong> — processing and maintaining your Gold, Platinum, or Diamond subscription.</li>
              <li><strong>Improve the service</strong> — analysing anonymised, aggregated usage data to enhance features and fix issues.</li>
              <li><strong>Communicate with you</strong> — sending account-related emails such as password resets or important service updates.</li>
            </ul>
            <p>We do <strong>not</strong> use your data for advertising or marketing purposes.</p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <ul>
              <li>All data is stored on <strong>Amazon Web Services (AWS)</strong> in the <strong>eu-west-2 (London)</strong> region.</li>
              <li>Data is <strong>encrypted at rest</strong> using AES-256 encryption.</li>
              <li>Data is <strong>encrypted in transit</strong> using TLS 1.2 or higher.</li>
              <li>Access to production data is restricted to authorised personnel only.</li>
              <li>We follow industry-standard security practices to protect your information.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>ChessMate uses the following third-party services to operate:</p>
            <ul>
              <li><strong>Amazon Cognito</strong> — user authentication and account management.</li>
              <li><strong>Amazon DynamoDB</strong> — secure storage of user data, game records, and ratings.</li>
              <li><strong>Stripe</strong> — processing subscription payments on the web.</li>
              <li><strong>Apple In-App Purchase</strong> — processing subscription payments on iOS.</li>
            </ul>
            <p>
              Each third-party service has its own privacy policy. We encourage you to review them.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <ul>
              <li>We <strong>do not sell</strong> your personal data to any third party.</li>
              <li>We <strong>do not share</strong> your data with advertisers.</li>
              <li>We share data <strong>only</strong> with the payment processors listed above (Stripe and Apple) to the extent necessary to process your subscription payments.</li>
              <li>We may disclose data if required to do so by law or in response to a valid legal request.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li>We retain your personal data for as long as your account remains active.</li>
              <li>If you request account deletion, we will delete your personal data within <strong>30 days</strong> of the request.</li>
              <li>Anonymised, aggregated data (e.g. overall usage statistics) may be retained after deletion as it cannot be linked back to you.</li>
              <li>To request account deletion, contact us at the email address below.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the UK General Data Protection Regulation (UK GDPR), you have the following rights:
            </p>
            <ul>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — request correction of any inaccurate or incomplete data.</li>
              <li><strong>Erasure</strong> — request deletion of your personal data ("right to be forgotten").</li>
              <li><strong>Data portability</strong> — request your data in a structured, commonly used, machine-readable format.</li>
              <li><strong>Restriction</strong> — request that we limit how we use your data.</li>
              <li><strong>Objection</strong> — object to our processing of your data in certain circumstances.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. We will
              respond within 30 days.
            </p>
            <p>
              If you are not satisfied with our response, you have the right to lodge a complaint with
              the Information Commissioner's Office (ICO) at https://ico.org.uk.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make significant changes, we
              will notify you via the email address associated with your account. The "Last Updated"
              date at the top of this policy indicates when it was last revised.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or your
              personal data, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default ChessMatePrivacy;
